import moment from 'moment';
import React from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { ActionIcon, Badge, Modal } from 'rizzui';
import { Maintenance } from '../../type/entities/maintenance-type';
import { maintenanceTypes } from '../../utils/SelectorMapping';

interface MaintenanceDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    maintenance: Maintenance | null;
}

const MaintenanceDetailModal: React.FC<MaintenanceDetailModalProps> = ({
    isOpen,
    onClose,
    maintenance
}) => {
    if (!maintenance) return null;

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const formatDate = (date: any) => {
        if (!date) return 'Sin fecha';
        const dateValue = date?.date || date;
        return moment(dateValue).format('DD/MM/YYYY');
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'Sin fecha';
        const dateValue = date?.date || date;
        return moment(dateValue).format('DD/MM/YYYY HH:mm');
    };

    const vehicle = maintenance.vehicle;
    const isSold = vehicle?.status === 'sold';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="m-auto px-7 pt-6 pb-8">
                <div className="mb-7 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Detalle de Mantenimiento
                    </h3>
                    <ActionIcon
                        size="sm"
                        variant="text"
                        onClick={onClose}
                        className="p-0 text-gray-500 hover:!text-gray-900"
                    >
                        <HiOutlineX className="h-[18px] w-[18px]" />
                    </ActionIcon>
                </div>

                <div className="space-y-6">
                    {/* Información del Vehículo */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Información del Vehículo</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Matrícula</label>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-gray-900">
                                        {vehicle?.plateNumber || 'Sin matrícula'}
                                    </p>
                                    {isSold && (
                                        <Badge color="danger" size="sm">
                                            Vendido
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Marca y Modelo</label>
                                <p className="text-sm text-gray-900">
                                    {vehicle?.brand} {vehicle?.model}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Información del Mantenimiento */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Detalles del Mantenimiento</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Tipo</label>
                                    <p className="text-sm text-gray-900">
                                        {maintenanceTypes.find(type => type.value === maintenance.type)?.label || 'Sin tipo'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Fecha</label>
                                    <p className="text-sm text-gray-900">
                                        {formatDate(maintenance.date)}
                                    </p>
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-700">Coste</label>
                                <p className="text-lg font-semibold text-gray-900">
                                    {maintenance.cost ? formatCurrency(parseFloat(maintenance.cost.toString())) : 'Sin coste'}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700">Kilómetros</label>
                                <p className="text-sm text-gray-900">
                                    {maintenance.odometerKm != null
                                        ? `${Number(maintenance.odometerKm).toLocaleString('es-ES')} km`
                                        : 'No indicado'}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700">Descripción</label>
                                <div className="mt-1 rounded-md border border-gray-200 p-3">
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                        {maintenance.description || 'Sin descripción'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Información de Auditoría */}
                    <div className="rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 font-medium text-gray-900">Información de Registro</h4>
                        <div className="grid grid-cols-1 gap-2">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Fecha de creación</label>
                                <p className="text-sm text-gray-900">
                                    {moment(maintenance.createdAt?.date).format('DD/MM/YYYY HH:mm')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex justify-end">
                    <button
                        onClick={onClose}
                        className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default MaintenanceDetailModal;